import React, { useRef, useState } from 'react';
import {
  ArrowLeft, ArrowRight, Camera, Check, ChevronDown, FileText, Info, Lock, ShieldCheck, Trash2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileUpload from '@/components/FileUpload';
import ReCAPTCHA from 'react-google-recaptcha';

/**
 * Dépôt du dossier — planches 7 (desktop) et 3 (mobile) de la maquette
 * « Refonte conversion ».
 *
 * Ce que la maquette inverse, et pourquoi :
 *  - le document d'abord, les coordonnées ensuite. On demande l'effort le moins
 *    coûteux en premier, et le formulaire d'identité n'apparaît qu'une fois le
 *    courrier déposé ;
 *  - le bouton de l'étape 1 dit « Continuer », pas « Lancer l'analyse » : on est
 *    encore à l'étape 1 sur 3 ;
 *  - le consentement avocat quitte l'encadré jaune d'alerte pour un bloc neutre
 *    marqué « Facultatif », avec la phrase qui lève le doute. Le téléphone, lui,
 *    reste obligatoire : il sert au suivi des prospects (écart assumé avec la
 *    planche 7, qui le voulait facultatif) ;
 *  - sur mobile, « Photographier » devient l'action principale — « glisser-déposer »
 *    ne veut rien dire sur téléphone — et le CTA est collé en bas.
 *
 * ⚠️ Le consentement est FACULTATIF ici. Le backend l'exigeait (400) : il a été
 * assoupli en conséquence. Un dossier sans consentement n'est pas publiable sur la
 * marketplace avocats — le filtre `consent_lawyer_share: True` s'en charge côté API.
 */

const ETAPES = [
  { n: 1, court: 'Courrier', long: 'Votre courrier' },
  { n: 2, court: 'Coordonnées', long: 'Vos coordonnées' },
  { n: 3, court: 'Analyse', long: 'Votre analyse' },
];

const RASSURANCE = [
  { icon: Lock, texte: 'Connexion chiffrée' },
  { icon: Trash2, texte: 'Supprimé sur simple demande' },
  { icon: ShieldCheck, texte: 'Aucune carte bancaire' },
];

const RECU = [
  'Votre score de contestabilité',
  'Les points à surveiller et les zones floues',
  'La liste des documents à retrouver',
];

const Stepper = ({ courant }) => (
  <div className="mb-6 flex items-center justify-center gap-2 sm:mb-8 sm:gap-3">
    {ETAPES.map((e, i) => (
      <React.Fragment key={e.n}>
        <div className="flex items-center gap-2">
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-bold"
            style={
              e.n <= courant
                ? { backgroundColor: '#1e3a8a', color: '#ffffff' }
                : { backgroundColor: '#e2e8f0', color: '#94a3b8' }
            }
          >
            {e.n < courant ? <Check size={14} strokeWidth={3} /> : e.n}
          </div>
          <div
            className="text-[12.5px] font-medium sm:text-[13.5px]"
            style={{ color: e.n <= courant ? '#1e293b' : '#94a3b8' }}
          >
            <span className="sm:hidden">{e.court}</span>
            <span className="hidden sm:inline">{e.long}</span>
          </div>
        </div>
        {i < ETAPES.length - 1 && (
          <div className="h-px w-4 shrink-0 sm:w-10" style={{ backgroundColor: '#e2e8f0' }} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const BlocConsentement = ({ checked, onChange }) => (
  <div
    className="flex items-start gap-3 rounded-xl border p-4"
    style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}
  >
    <input
      id="consent-lawyer-share"
      data-testid="consent-lawyer-share"
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-[3px] h-5 w-5 shrink-0 cursor-pointer accent-blue-800"
    />
    <label htmlFor="consent-lawyer-share" className="cursor-pointer">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[14.5px] font-semibold" style={{ color: '#1e293b' }}>
          Je souhaite aussi être mis en relation avec un avocat pour qu'il analyse mon dossier
        </span>
      </div>
      <div className="mt-[6px] text-[13px] leading-relaxed" style={{ color: '#64748b' }}>
        Si vous ne cochez pas cette case, vous recevez quand même votre analyse complète. Si vous la
        cochez, vos coordonnées sont transmises à notre avocat partenaire pour une proposition de
        consultation — révocable à tout moment. Voir la{' '}
        <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#1e3a8a' }}>
          politique de confidentialité
        </a>
        .
      </div>
    </label>
  </div>
);

const DepotForm = ({
  files, onFilesChange, formData, setFormData,
  consentLawyerShare, setConsentLawyerShare,
  recaptchaRef, setRecaptchaToken,
  loading, onSubmit, onFormStart, onFieldBlur,
}) => {
  const [etape, setEtape] = useState(1);
  const [aideOuverte, setAideOuverte] = useState(false);
  const cameraRef = useRef(null);

  const aucunFichier = files.length === 0;

  const capturer = (e) => {
    const pris = Array.from(e.target.files || []);
    if (pris.length) onFilesChange([...files, ...pris]);
    e.target.value = '';
  };

  return (
    <div className="mx-auto max-w-[820px] px-[18px] sm:px-6">
      <div className="mb-6 text-center sm:mb-8">
        <h2 className="m-0 text-[26px] font-extrabold leading-tight tracking-tight sm:text-[34px]" style={{ color: '#1e3a8a' }}>
          Déposez vos documents
        </h2>
        <p className="mx-auto mt-3 max-w-[560px] text-[15px] leading-relaxed sm:text-[17px]" style={{ color: '#475569', textWrap: 'pretty' }}>
          <span className="sm:hidden">Une photo prise au téléphone suffit.</span>
          <span className="hidden sm:inline">
            On commence par le document. Vos coordonnées ne seront demandées qu'à la fin, pour vous
            envoyer le rapport.
          </span>
        </p>
      </div>

      <Stepper courant={etape} />

      <form data-testid="upload-form" onSubmit={onSubmit}>
        <div
          className="rounded-2xl border bg-white p-5 sm:p-8"
          style={{ borderColor: '#e2e8f0', boxShadow: '0 12px 28px -18px rgba(15, 23, 42, 0.2)' }}
        >
          {etape === 1 ? (
            <div className="flex flex-col gap-5">
              {/* Mobile : capture photo en action principale */}
              <div className="grid grid-cols-2 gap-3 sm:hidden">
                <button
                  type="button"
                  onClick={() => cameraRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-2 rounded-xl px-3 py-5 text-[14px] font-semibold text-white"
                  style={{ backgroundColor: '#1e3a8a' }}
                >
                  <Camera size={22} />
                  Photographier
                </button>
                <label
                  className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border px-3 py-5 text-[14px] font-semibold"
                  style={{ borderColor: '#cbd5e1', color: '#1e293b' }}
                >
                  <FileText size={22} style={{ color: '#475569' }} />
                  Choisir un PDF
                  <input
                    type="file" accept="application/pdf,image/*" multiple className="hidden"
                    onChange={capturer}
                  />
                </label>
                <input
                  ref={cameraRef} type="file" accept="image/*" capture="environment" multiple
                  className="hidden" onChange={capturer}
                />
              </div>

              {/* Desktop : le glisser-déposer existant */}
              <div className="hidden sm:block">
                <FileUpload files={files} setFiles={onFilesChange} />
              </div>
              {/* Mobile : liste des fichiers déposés, sans la zone de drag */}
              {files.length > 0 && (
                <div className="flex flex-col gap-2 sm:hidden">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-3 rounded-xl border px-3 py-[10px]"
                      style={{ borderColor: '#bbf7d0', backgroundColor: '#f0fdf4' }}
                    >
                      <Check size={18} className="shrink-0" style={{ color: '#16a34a' }} />
                      <div className="min-w-0 flex-grow">
                        <div className="truncate text-[13.5px] font-medium" style={{ color: '#1e293b' }}>{f.name}</div>
                        <div className="text-[11.5px]" style={{ color: '#15803d' }}>
                          {(f.size / 1048576).toFixed(1)} Mo · prêt à analyser
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onFilesChange(files.filter((_, j) => j !== i))}
                        aria-label={`Retirer ${f.name}`}
                        style={{ color: '#64748b' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Aide repliée : plus un mur de texte */}
              <div className="rounded-xl border" style={{ borderColor: '#e2e8f0' }}>
                <button
                  type="button"
                  onClick={() => setAideOuverte((v) => !v)}
                  aria-expanded={aideOuverte}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                >
                  <span className="flex items-start gap-2 text-[13.5px]" style={{ color: '#1e293b' }}>
                    <Info size={16} className="mt-[2px] shrink-0" style={{ color: '#1e3a8a' }} />
                    {/* Empilé sous sm : à 390 px, la mise côte à côte hachait le libellé. */}
                    <span className="flex flex-col sm:flex-row sm:gap-1">
                      <span>Quels documents joindre&nbsp;?</span>
                      <span style={{ color: '#64748b' }}>Plus il y en a, plus l'analyse est fiable.</span>
                    </span>
                  </span>
                  <ChevronDown size={17} className={aideOuverte ? 'rotate-180' : ''} style={{ color: '#64748b' }} />
                </button>
                {aideOuverte && (
                  <ul className="border-t px-4 py-3 text-[13px] leading-relaxed" style={{ borderColor: '#f1f5f9', color: '#475569' }}>
                    <li>· La lettre de recouvrement ou la mise en demeure reçue</li>
                    <li>· Le courrier d'huissier ou de cabinet de recouvrement</li>
                    <li>· Le contrat ou le relevé lié à la dette, si vous l'avez</li>
                    <li>· Tout autre document lié à cette créance</li>
                  </ul>
                )}
              </div>

              {/* Aperçu de l'étape suivante : prouve que le formulaire est court */}
              <div className="hidden rounded-xl border p-4 sm:block" style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}>
                <div className="mb-3 flex items-center gap-2">
                  <div
                    className="flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-bold"
                    style={{ backgroundColor: '#e2e8f0', color: '#64748b' }}
                  >
                    2
                  </div>
                  <div className="text-[13.5px] font-semibold" style={{ color: '#475569' }}>
                    Ensuite — trois champs, rien de plus
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {['Prénom', 'E-mail', 'Téléphone'].map((l) => (
                    <div key={l}>
                      <div className="mb-1 text-[12px]" style={{ color: '#94a3b8' }}>{l}</div>
                      <div className="h-8 rounded-md border" style={{ borderColor: '#e2e8f0', backgroundColor: '#ffffff' }} />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2 text-[12.5px]" style={{ color: '#64748b' }}>
                  <Info size={14} className="shrink-0" />
                  Votre analyse arrive par e-mail. Le téléphone sert à vous joindre si le dossier
                  nécessite une précision.
                </div>
              </div>

              {/* Mobile : ce que vous recevrez, juste avant le bouton */}
              <div className="rounded-xl border p-4 sm:hidden" style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}>
                <div className="mb-2 text-[13px] font-semibold" style={{ color: '#1e293b' }}>Ce que vous recevrez</div>
                {RECU.map((r) => (
                  <div key={r} className="flex items-start gap-2 py-[3px]">
                    <Check size={15} className="mt-[2px] shrink-0" style={{ color: '#16a34a' }} />
                    <span className="text-[13px]" style={{ color: '#475569' }}>{r}</span>
                  </div>
                ))}
              </div>

              <BlocConsentement checked={consentLawyerShare} onChange={setConsentLawyerShare} />

              <button
                type="button"
                disabled={aucunFichier}
                onClick={() => { onFormStart?.(); setEtape(2); }}
                data-testid="depot-continuer"
                className="inline-flex w-full items-center justify-center gap-[10px] rounded-full px-6 py-[15px] text-[16px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-40"
                style={{ backgroundColor: '#1e3a8a', boxShadow: '0 10px 15px -3px rgba(30, 58, 138, 0.24)' }}
              >
                Continuer
                <ArrowRight size={18} strokeWidth={2.2} />
              </button>
              {aucunFichier && (
                <div className="-mt-2 text-center text-[12.5px]" style={{ color: '#94a3b8' }}>
                  Ajoutez d'abord vos documents pour continuer.
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <button
                type="button"
                onClick={() => setEtape(1)}
                className="inline-flex items-center gap-2 self-start text-[13.5px] font-medium"
                style={{ color: '#1e3a8a' }}
              >
                <ArrowLeft size={16} />
                {files.length} document{files.length > 1 ? 's' : ''} déposé{files.length > 1 ? 's' : ''} — modifier
              </button>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="nom">Prénom et nom *</Label>
                  <Input
                    id="nom" data-testid="input-nom" type="text" required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    onFocus={onFormStart}
                    onBlur={(e) => onFieldBlur('nom', e.target.value)}
                    placeholder="Votre nom" className="mt-2" autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email" data-testid="input-email" type="email" required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onFocus={onFormStart}
                    onBlur={(e) => onFieldBlur('email', e.target.value)}
                    placeholder="votre@email.fr" className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="telephone">
                  Téléphone *{' '}
                  <span className="font-normal" style={{ color: '#64748b' }}>
                    — pour vous joindre si votre dossier nécessite une précision
                  </span>
                </Label>
                <Input
                  id="telephone" data-testid="input-telephone" type="tel" required
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  onFocus={onFormStart}
                  onBlur={(e) => onFieldBlur('telephone', e.target.value)}
                  placeholder="06 12 34 56 78" className="mt-2"
                />
              </div>

              {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={(token) => setRecaptchaToken(token)}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                </div>
              )}

              <button
                type="submit"
                data-testid="submit-dossier-button"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-[10px] rounded-full px-6 py-[15px] text-[16px] font-semibold text-white transition disabled:opacity-60"
                style={{ backgroundColor: '#1e3a8a', boxShadow: '0 10px 15px -3px rgba(30, 58, 138, 0.24)' }}
              >
                {loading ? 'Envoi et analyse en cours…' : 'Obtenir mon analyse gratuite'}
                {!loading && <ArrowRight size={18} strokeWidth={2.2} />}
              </button>

              {/* La réassurance remplace l'avertissement juste au-dessus du clic */}
              <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
                {RASSURANCE.map(({ icon: Icon, texte }) => (
                  <div key={texte} className="flex items-center gap-[6px] text-[12.5px]" style={{ color: '#64748b' }}>
                    <Icon size={14} className="shrink-0" style={{ color: '#16a34a' }} />
                    {texte}
                  </div>
                ))}
              </div>

              {loading && (
                <div className="rounded-xl border p-4 text-center" style={{ borderColor: '#bfdbfe', backgroundColor: '#eff6ff' }}>
                  <div className="text-[14px] font-semibold" style={{ color: '#1e3a8a' }}>
                    {files.length} fichier{files.length > 1 ? 's' : ''} en cours d'analyse
                  </div>
                  <div className="mt-1 text-[13px]" style={{ color: '#1e40af' }}>
                    Comptez 1 à 3 minutes. Ne fermez pas cette page.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </form>

      <div className="mt-4 flex items-start justify-center gap-2 text-[12.5px]" style={{ color: '#64748b' }}>
        <Info size={14} className="mt-[2px] shrink-0" />
        <span>
          Cette analyse est un diagnostic, pas un verdict juridique. Elle ne remplace pas les
          conseils d'un avocat.
        </span>
      </div>
    </div>
  );
};

export default DepotForm;
